/** @flow */

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import actionCreators from "@modules/service/actionCreator";
import { getSchema } from "@utils/schema";
import {
  GetEffectiveUserPermissions,
  GetEffectiveUserPermissionsSchema,
  UpdateEffectiveUserPermissions,
} from "@modules/service/constants/actions";
import type { EffectiveUserPermissions } from "@modules/service/reducer/user-permissions/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { isFirstCareUser, isClientAdmin } from "@modules/account/selectors";

type EffectiveUserPermissionsRequest = $Exact<{
  clientId: string,
}>;

type UpdateEffectiveUserPermissionsRequest = $Exact<{
  userPermissions: EffectiveUserPermissions,
  clientId: string,
  successMessage: string,
}>;

export function* onGetEffectiveUserPermissions({
  payload,
}: Action<EffectiveUserPermissionsRequest>): Generator<
  *,
  void,
  { data: EffectiveUserPermissions },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/user-permissions`,
    );

    yield put(actionCreators.GetEffectiveUserPermissionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEffectiveUserPermissionsError(e.response));
  }
}

export function* onGetEffectiveUserPermissionsSchema(): Generator<
  *,
  void,
  EffectiveUserPermissions,
> {
  try {
    const data = getSchema("UserPermissionsServiceSectionDTO");

    yield put(actionCreators.GetEffectiveUserPermissionsSchemaSuccess(data));
  } catch (e) {
    yield put(actionCreators.GetEffectiveUserPermissionsSchemaError(e.response));
  }
}

export function* onUpdateEffectiveUserPermissions({
  payload,
}: Action<UpdateEffectiveUserPermissionsRequest>): Generator<
  *,
  void,
  { data: EffectiveUserPermissions },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/user-permissions`,
      payload.userPermissions,
    );

    yield all([
      put(actionCreators.UpdateEffectiveUserPermissionsSuccess(response.data)),
      put(actionCreators.ToggleEmployeeUsersModal(false)),
      put(sendSuccessMessage("USER_PERMISSIONS_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateEffectiveUserPermissionsError(e.response));
  }
}

export function* UserPermissionsSagas(): Generator<*, void, void> {
  yield all([takeEvery(GetEffectiveUserPermissions, onGetEffectiveUserPermissions)]);
  yield all([takeEvery(GetEffectiveUserPermissionsSchema, onGetEffectiveUserPermissionsSchema)]);
  yield all([takeEvery(UpdateEffectiveUserPermissions, onUpdateEffectiveUserPermissions)]);
}

export default UserPermissionsSagas;
